import { useEffect, useMemo, useState, type ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  Bell,
  Check,
  Copy,
  Database,
  FileCode2,
  Folder,
  FolderOpen,
  GitBranch,
  LayoutDashboard,
  LayoutGrid,
  Link2,
  Linkedin,
  Plus,
  RefreshCw,
  Search,
  Shield,
  Star,
  Terminal,
  Workflow,
  X,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { productsCopy, productsCardsCopy } from "@/lib/siteCopy";
import { teamCopy } from "@/lib/aboutPageCopy";

const ease = [0.22, 1, 0.36, 1] as const;

type Asset = {
  id: string;
  name: string;
  modified: string;
  tone: string;
  image: string;
  detail: string;
};

type TeamMember = {
  name: string;
  role: string;
  bio: string;
  src: string;
  linkedin: string;
};

const TEAM_MEMBERS_BASE = [
  {
    name: "Omkar Biradar",
    role: "CEO",
    src: "https://res.cloudinary.com/zeqof4ic/image/upload/v1783853257/omkarddcimage_lupe9z.png",
    linkedin: "https://www.linkedin.com/in/omkar-biradar-457725245",
  },
  {
    name: "Amol Rakh",
    role: "CTO",
    src: "https://res.cloudinary.com/zeqof4ic/image/upload/v1783850830/amolddcimg2-removebg-preview_ip2raa.png",
    linkedin: "https://www.linkedin.com/in/amolrakha22",
  },
  {
    name: "Karan Lande",
    role: "COO",
    src: "https://res.cloudinary.com/zeqof4ic/image/upload/v1783851059/karanddcimg-removebg-preview_ofadw1.png",
    linkedin: "https://www.linkedin.com/in/karan-lande-3326a431b",
  },
] as const;

const SIDEBAR_ITEMS = [
  { id: "dashboard", icon: LayoutDashboard, brand: false as const },
  { id: "drafts", icon: Folder, brand: false as const },
  { id: "cli", icon: Terminal, brand: true as const, label: "DevCLI" },
  { id: "sync", icon: Database, brand: true as const, label: "DataSync" },
] as const;

const ASSETS: Asset[] = [
  {
    id: "auth",
    name: "Auth flow",
    modified: "2h ago",
    tone: "bg-[#E7F0EC]",
    image: "/productsection/authflow.png",
    detail: "SSO + MFA · 3 providers connected",
  },
  {
    id: "sync",
    name: "Sync job",
    modified: "5h ago",
    tone: "bg-[#E8EEF5]",
    image: "/productsection/syncflow.png",
    detail: "Realtime mirror · 12ms lag",
  },
  {
    id: "cli",
    name: "CLI pack",
    modified: "1d ago",
    tone: "bg-[#F3EFEA]",
    image: "/productsection/clipack.png",
    detail: "ddc deploy · 14 scripts",
  },
  {
    id: "pipe",
    name: "Pipeline",
    modified: "2d ago",
    tone: "bg-[#FFF4DE]",
    image: "/productsection/pipeline.png",
    detail: "CI/CD · last run passed",
  },
  {
    id: "hooks",
    name: "Webhooks",
    modified: "3d ago",
    tone: "bg-[#F2E8F8]",
    image: "/productsection/webhooks.png",
    detail: "8 endpoints · signed payloads",
  },
  {
    id: "dash",
    name: "Dash kit",
    modified: "4d ago",
    tone: "bg-[#1A1A1A]",
    image: "/productsection/dashkit.png",
    detail: "Analytics widgets · dark theme",
  },
];

const FOLDER_FILTER: Record<string, string[] | "all"> = {
  dashboard: "all",
  drafts: ["cli", "pipe"],
  cli: ["cli"],
  sync: ["sync", "hooks"],
  project: "all",
};

const INTEGRATIONS = [
  { id: "figma", label: "Figma", icon: LayoutGrid },
  { id: "vscode", label: "VS Code", icon: FileCode2 },
  { id: "github", label: "GitHub", icon: GitBranch },
  { id: "slack", label: "Slack", icon: Bell },
  { id: "vercel", label: "Vercel", icon: Star },
  { id: "aws", label: "AWS", icon: Database },
] as const;

const CODE_BY_SDK = {
  react: `<SecureAuth
  project="acme-prod"
  providers={["google", "sso"]}
/>`,
  node: `await dataSync.push({
  channel: "orders",
  payload: event,
});`,
  cli: `$ ddc deploy --env prod
✓ synced 14 services`,
} as const;

type SdkId = keyof typeof CODE_BY_SDK;

function BentoCard({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "relative flex min-h-0 w-full max-w-full flex-col overflow-hidden rounded-[1.25rem] border border-black/[0.06] bg-[#F4F4F5] dark:border-white/[0.08] dark:bg-[#141414] sm:rounded-[1.75rem]",
        className,
      )}
    >
      {children}
    </div>
  );
}

function CardCopy({
  title,
  body,
  className,
}: {
  title: string;
  body: string;
  className?: string;
}) {
  return (
    <div className={cn("relative z-10 px-4 pt-5 sm:px-7 sm:pt-7", className)}>
      <h3 className="text-[1.2rem] font-semibold tracking-[-0.03em] text-neutral-900 dark:text-white sm:text-2xl">
        {title}
      </h3>
      <p className="mt-2 max-w-md text-[14px] leading-relaxed text-neutral-500 dark:text-neutral-400 sm:text-[15px]">
        {body}
      </p>
    </div>
  );
}

/* ─── 1. Workspace ─────────────────────────────────────────── */

function WorkspaceCard() {
  const lang = useSiteLanguage();
  const card = productsCardsCopy[lang].workspace;
  const ui = productsCardsCopy[lang].ui;
  const bios = teamCopy[lang].bios;
  const teamMembers: TeamMember[] = TEAM_MEMBERS_BASE.map((m, i) => ({
    ...m,
    bio: bios[i],
  }));
  const sidebar = SIDEBAR_ITEMS.map((item) => {
    const label =
      item.id === "dashboard"
        ? ui.dashboard
        : item.id === "drafts"
          ? ui.drafts
          : item.label;
    return { id: item.id, icon: item.icon, label };
  });
  const [nav, setNav] = useState<string>("dashboard");
  const [selected, setSelected] = useState<string>("auth");
  const [query, setQuery] = useState("");
  const [starred, setStarred] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [profile, setProfile] = useState<TeamMember | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 1800);
  };

  const toastOpened = (id: string, label: string) => {
    if (id === "dashboard") return ui.openedDashboard;
    if (id === "drafts") return ui.openedDrafts;
    return ui.openedItem.replace("{name}", label);
  };

  const filtered = useMemo(() => {
    const ids = FOLDER_FILTER[nav] ?? "all";
    return ASSETS.filter((a) => {
      const inFolder = ids === "all" || ids.includes(a.id);
      const q = query.trim().toLowerCase();
      const matches = !q || a.name.toLowerCase().includes(q) || a.detail.toLowerCase().includes(q);
      return inFolder && matches;
    });
  }, [nav, query]);

  const active = ASSETS.find((a) => a.id === selected) ?? ASSETS[0];

  useEffect(() => {
    if (!profile) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setProfile(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [profile]);

  return (
    <BentoCard className="lg:col-span-2 lg:row-span-2">
      <CardCopy
        title={card.title}
        body={card.body}
      />

      <div className="relative mt-4 flex min-h-[18rem] flex-1 overflow-hidden px-3 pb-3 sm:mt-6 sm:min-h-[26rem] sm:px-5 sm:pb-5 md:px-6 md:pb-6">
        <div className="relative flex w-full min-w-0 flex-col overflow-hidden rounded-xl border border-black/[0.08] bg-white shadow-[0_16px_40px_rgba(0,0,0,0.06)] dark:border-white/[0.1] dark:bg-[#1A1A1A] dark:shadow-[0_16px_40px_rgba(0,0,0,0.35)] sm:flex-row sm:rounded-2xl">
          {/* Sidebar - horizontal chips on mobile, vertical on sm+ */}
          <aside className="flex w-full shrink-0 flex-col border-b border-black/[0.06] bg-[#FAFAFA] dark:border-white/[0.08] dark:bg-[#111] sm:w-[9.5rem] sm:border-b-0 sm:border-r sm:p-3">
            <div className="mb-0 flex items-center gap-2 px-3 py-2.5 sm:mb-4 sm:px-1 sm:py-0">
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-neutral-900 text-[10px] font-bold text-white dark:bg-white dark:text-black">
                D
              </span>
              <span className="text-xs font-semibold tracking-tight">DevDotCom</span>
            </div>
            <nav className="flex gap-1 overflow-x-auto px-2 pb-2 sm:block sm:space-y-0.5 sm:overflow-visible sm:px-0 sm:pb-0">
              {sidebar.map((item) => {
                const Icon = item.icon;
                const isActive = nav === item.id;
                return (
                  <motion.button
                    key={item.id}
                    type="button"
                    whileTap={{ scale: 0.97 }}
                    onClick={() => {
                      setNav(item.id);
                      showToast(toastOpened(item.id, item.label));
                    }}
                    className={cn(
                      "inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-left text-[11px] transition-colors sm:w-full sm:gap-2 sm:px-2",
                      isActive
                        ? "bg-black/[0.06] font-medium text-neutral-900 dark:bg-white/[0.08] dark:text-white"
                        : "text-neutral-500 hover:bg-black/[0.04] dark:text-neutral-400 dark:hover:bg-white/[0.05]",
                    )}
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
                    {item.label}
                  </motion.button>
                );
              })}
              <motion.button
                type="button"
                whileTap={{ scale: 0.97 }}
                onClick={() => {
                  setNav("project");
                  showToast(ui.openedAcme);
                }}
                className={cn(
                  "inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] sm:mt-3 sm:w-full sm:gap-2 sm:border-t sm:border-black/[0.06] sm:px-2 sm:pt-3 dark:sm:border-white/[0.08]",
                  nav === "project"
                    ? "bg-black/[0.06] font-medium dark:bg-white/[0.08]"
                    : "text-neutral-500 hover:bg-black/[0.04]",
                )}
              >
                <FolderOpen className="h-3.5 w-3.5" />
                {ui.acmeProd}
              </motion.button>
            </nav>
          </aside>

          {/* Main */}
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex flex-wrap items-center gap-2 border-b border-black/[0.06] px-2.5 py-2 dark:border-white/[0.08] sm:flex-nowrap sm:px-3 sm:py-2.5">
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                className="flex min-w-0 flex-1 basis-[min(100%,8rem)] items-center gap-2 rounded-lg bg-black/[0.04] px-2.5 py-1.5 text-left dark:bg-white/[0.06]"
              >
                <Search className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
                {searchOpen ? (
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onBlur={() => {
                      if (!query) setSearchOpen(false);
                    }}
                    placeholder={ui.searchPlaceholder}
                    className="min-w-0 flex-1 bg-transparent text-[11px] outline-none placeholder:text-neutral-400"
                  />
                ) : (
                  <span className="truncate text-[11px] text-neutral-400">
                    {ui.searchProjects}
                  </span>
                )}
              </button>
              <div className="flex items-center -space-x-1.5">
                {teamMembers.map((member) => (
                  <motion.button
                    key={member.name}
                    type="button"
                    whileHover={{ y: -2, zIndex: 2 }}
                    whileTap={{ scale: 0.92 }}
                    onClick={() => setProfile(member)}
                    className="relative h-7 w-7 overflow-hidden rounded-full border-2 border-white dark:border-[#1A1A1A]"
                    aria-label={`View ${member.name}'s profile`}
                  >
                    <img
                      src={member.src}
                      alt=""
                      className="h-full w-full object-cover object-top"
                    />
                  </motion.button>
                ))}
              </div>
              <motion.button
                type="button"
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#00C853] px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-sm sm:px-3"
                whileTap={{ scale: 0.96 }}
                whileHover={{ scale: 1.03 }}
                onClick={() => {
                  setSelected("auth");
                  setNav("dashboard");
                  showToast(ui.projectCreated);
                }}
              >
                <Plus className="h-3 w-3" strokeWidth={2.5} />
                <span className="sm:hidden">{ui.newShort}</span>
                <span className="hidden sm:inline">{ui.newProject}</span>
              </motion.button>
            </div>

            {/* Selection strip */}
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2 border-b border-black/[0.05] bg-black/[0.02] px-3 py-1.5 dark:border-white/[0.06] dark:bg-white/[0.03]"
              >
                <img
                  src={active.image}
                  alt=""
                  className="h-4 w-4 shrink-0 rounded object-cover"
                />
                <p className="min-w-0 flex-1 truncate text-[10px] text-neutral-500">
                  <span className="font-semibold text-neutral-800 dark:text-neutral-200">
                    {active.name}
                  </span>
                  {" · "}
                  {active.detail}
                </p>
                <motion.button
                  type="button"
                  whileTap={{ scale: 0.9 }}
                  onClick={() => {
                    setStarred((s) => ({ ...s, [active.id]: !s[active.id] }));
                    showToast(
                      starred[active.id] ? ui.unstarred : ui.starred,
                    );
                  }}
                  className="rounded-md p-1 hover:bg-black/[0.05] dark:hover:bg-white/[0.06]"
                  aria-label="Star asset"
                >
                  <Star
                    className={cn(
                      "h-3.5 w-3.5",
                      starred[active.id]
                        ? "fill-amber-400 text-amber-400"
                        : "text-neutral-400",
                    )}
                  />
                </motion.button>
              </motion.div>
            </AnimatePresence>

            <div className="grid flex-1 grid-cols-2 content-start gap-2 overflow-y-auto p-2.5 sm:grid-cols-3 sm:gap-3 sm:p-4">
              <AnimatePresence mode="popLayout">
                {filtered.map((asset, i) => {
                  const isSelected = selected === asset.id;
                  return (
                    <motion.button
                      key={asset.id}
                      layout
                      type="button"
                      initial={{ opacity: 0, y: 10, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ delay: i * 0.03, duration: 0.35, ease }}
                      whileHover={{ y: -3, scale: 1.02 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => {
                        setSelected(asset.id);
                        showToast(
                          ui.openedItem.replace("{name}", asset.name),
                        );
                      }}
                      className={cn(
                        "group flex flex-col overflow-hidden rounded-xl border bg-white text-left shadow-sm transition-shadow dark:bg-[#222]",
                        isSelected
                          ? "border-[#00C853] ring-2 ring-[#00C853]/25 shadow-md"
                          : "border-black/[0.06] hover:shadow-md dark:border-white/[0.08]",
                      )}
                    >
                      <div
                        className={cn(
                          "relative aspect-[4/3] overflow-hidden",
                          asset.tone,
                        )}
                      >
                        <motion.img
                          src={asset.image}
                          alt=""
                          animate={
                            isSelected
                              ? { scale: [1, 1.04, 1] }
                              : { scale: 1 }
                          }
                          transition={{ duration: 0.45 }}
                          className="h-full w-full object-cover object-center"
                        />
                        {starred[asset.id] && (
                          <span className="absolute right-2 top-2 rounded-full bg-white/90 p-0.5 shadow-sm dark:bg-black/50">
                            <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                          </span>
                        )}
                      </div>
                      <div className="px-2.5 py-2">
                        <p className="truncate text-[11px] font-semibold tracking-tight">
                          {asset.name}
                        </p>
                        <p className="text-[10px] text-neutral-400">
                          Modified {asset.modified}
                        </p>
                      </div>
                    </motion.button>
                  );
                })}
              </AnimatePresence>
              {filtered.length === 0 && (
                <p className="col-span-full py-8 text-center text-[12px] text-neutral-400">
                  {ui.noAssets.replace("{query}", query)}
                </p>
              )}
            </div>
          </div>

          <AnimatePresence>
            {toast && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                className="absolute bottom-3 left-1/2 z-20 -translate-x-1/2 rounded-full bg-neutral-900 px-3 py-1.5 text-[11px] font-medium text-white shadow-lg dark:bg-white dark:text-black"
              >
                {toast}
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {profile && (
              <motion.div
                key="profile-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 z-30 flex items-end justify-center bg-black/45 p-3 sm:items-center sm:p-5"
                onClick={() => setProfile(null)}
                role="presentation"
              >
                <motion.div
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="workspace-profile-name"
                  initial={{ opacity: 0, y: 24, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 16, scale: 0.97 }}
                  transition={{ duration: 0.28, ease }}
                  onClick={(e) => e.stopPropagation()}
                  className="relative flex w-full max-w-[20rem] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-[#1A1A1A] sm:max-w-[22rem]"
                >
                  <button
                    type="button"
                    onClick={() => setProfile(null)}
                    className="absolute right-2.5 top-2.5 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm transition-colors hover:bg-black/55"
                    aria-label={ui.closeProfile}
                  >
                    <X className="h-4 w-4" strokeWidth={2} />
                  </button>
                  <div className="aspect-[5/4] w-full overflow-hidden bg-[#F3F4F6]">
                    <img
                      src={profile.src}
                      alt={profile.name}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="space-y-3 p-4 sm:p-5">
                    <div>
                      <h4
                        id="workspace-profile-name"
                        className="text-lg font-semibold tracking-tight text-neutral-900 dark:text-white"
                      >
                        {profile.name}
                      </h4>
                      <p className="mt-0.5 text-sm font-medium text-[#00C853]">
                        {profile.role}
                      </p>
                    </div>
                    <p className="text-[13px] leading-relaxed text-neutral-500 dark:text-neutral-400">
                      {profile.bio}
                    </p>
                    <a
                      href={profile.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-full border border-black/[0.08] bg-black/[0.03] px-3.5 py-2 text-[12px] font-medium text-neutral-800 transition-colors hover:bg-black/[0.06] dark:border-white/10 dark:bg-white/[0.06] dark:text-white dark:hover:bg-white/[0.1]"
                    >
                      <Linkedin className="h-3.5 w-3.5" strokeWidth={1.75} />
                      {ui.viewLinkedIn}
                    </a>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </BentoCard>
  );
}

/* ─── 2. Optimize ──────────────────────────────────────────── */

function OptimizeCard() {
  const lang = useSiteLanguage();
  const card = productsCardsCopy[lang].optimize;
  const ui = productsCardsCopy[lang].ui;
  const [pct, setPct] = useState(0);
  const [size, setSize] = useState("11.90");
  const [run, setRun] = useState(0);
  const [optimizing, setOptimizing] = useState(false);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (reduce) {
      setPct(92);
      return;
    }
    setPct(0);
    const id = window.setTimeout(() => setPct(88 + (run % 5)), 120);
    return () => window.clearTimeout(id);
  }, [reduce, run]);

  const optimize = () => {
    if (optimizing) return;
    setOptimizing(true);
    setPct(0);
    const next = (10.2 + Math.random() * 2.4).toFixed(2);
    window.setTimeout(() => {
      setSize(next);
      setRun((r) => r + 1);
      setOptimizing(false);
    }, 700);
  };

  return (
    <BentoCard className="min-h-[16rem]">
      <CardCopy
        title={card.title}
        body={card.body}
      />
      <div className="relative flex flex-1 items-center justify-center px-6 pb-8 pt-4">
        <motion.button
          type="button"
          onClick={optimize}
          className="w-full max-w-[16rem] rounded-2xl border border-black/[0.08] bg-white p-4 text-left shadow-[0_12px_32px_rgba(0,0,0,0.08)] dark:border-white/[0.1] dark:bg-[#1A1A1A]"
          animate={reduce || optimizing ? undefined : { y: [0, -6, 0] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-medium text-neutral-500">{ui.bundleSize}</p>
            <motion.span
              animate={optimizing ? { rotate: 360 } : { rotate: 0 }}
              transition={
                optimizing
                  ? { duration: 0.7, repeat: Infinity, ease: "linear" }
                  : { duration: 0.2 }
              }
            >
              <RefreshCw className="h-3.5 w-3.5 text-neutral-400" />
            </motion.span>
          </div>
          <p className="mt-2 font-mono text-2xl font-semibold tracking-tight">
            {size} KB
          </p>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-black/[0.06] dark:bg-white/[0.08]">
            <motion.div
              className="h-full rounded-full bg-[#00C853]"
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 1.1, ease }}
            />
          </div>
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#00C853]/12 px-2.5 py-1 text-[11px] font-semibold text-[#008C3A] dark:text-[#3DDB7A]">
            <Check className="h-3 w-3" strokeWidth={2.5} />
            {ui.smallerBaseline.replace("{pct}", String(pct))}
          </div>
          <p className="mt-2 text-[10px] text-neutral-400">{ui.clickReoptimize}</p>
        </motion.button>
      </div>
    </BentoCard>
  );
}

/* ─── 3. Progress ──────────────────────────────────────────── */

function ProgressCard() {
  const lang = useSiteLanguage();
  const card = productsCardsCopy[lang].progress;
  const ui = productsCardsCopy[lang].ui;
  const reduce = useReducedMotion();
  const [count, setCount] = useState(2);
  const [open, setOpen] = useState(false);
  const alerts = [
    { title: ui.deployReady, meta: "prod · 2m ago" },
    { title: ui.reviewRequested, meta: "auth-flow · 8m ago" },
    { title: ui.syncHealthy, meta: "orders · 12m ago" },
  ];

  return (
    <BentoCard className="min-h-[16rem]">
      <CardCopy
        title={card.title}
        body={card.body}
      />
      <div className="relative flex flex-1 flex-col items-center justify-center px-6 pb-6 pt-2">
        <motion.button
          type="button"
          className="relative"
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.96 }}
          onClick={() => {
            setOpen((o) => !o);
            if (!open) setCount((c) => Math.max(0, c - 1));
          }}
        >
          <motion.div
            className="flex h-28 w-28 items-center justify-center rounded-[2rem] bg-gradient-to-br from-[#E8EEF5] to-[#F2E8F8] shadow-inner dark:from-[#1E2430] dark:to-[#2A1F33]"
            animate={reduce ? undefined : { rotate: [0, 2, -2, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          >
            <Bell className="h-12 w-12 text-neutral-800/80 dark:text-white/80" strokeWidth={1.25} />
          </motion.div>

          <motion.div
            className="absolute -right-3 -top-2 flex h-9 w-9 items-center justify-center rounded-full bg-[#00C853] text-sm font-bold text-white shadow-lg"
            key={count}
            initial={{ scale: 0.7 }}
            animate={{ scale: 1 }}
          >
            {count}
            {!reduce && count > 0 && (
              <span className="absolute inset-0 animate-ping rounded-full bg-[#00C853]/40" />
            )}
          </motion.div>
        </motion.button>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: 8, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              exit={{ opacity: 0, y: 6, height: 0 }}
              className="mt-4 w-full max-w-[15rem] overflow-hidden"
            >
              <div className="space-y-1.5 rounded-xl border border-black/[0.08] bg-white p-2 shadow-sm dark:border-white/[0.1] dark:bg-[#1A1A1A]">
                {alerts.slice(0, Math.max(1, count || 1)).map((a) => (
                  <button
                    key={a.title}
                    type="button"
                    onClick={() => setCount((c) => Math.min(3, c + 1))}
                    className="flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-black/[0.04] dark:hover:bg-white/[0.05]"
                  >
                    <Check className="mt-0.5 h-3 w-3 shrink-0 text-[#00C853]" />
                    <span>
                      <span className="block text-[11px] font-medium">{a.title}</span>
                      <span className="text-[10px] text-neutral-400">{a.meta}</span>
                    </span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {!open && (
          <p className="mt-3 text-[10px] text-neutral-400">{ui.clickAlerts}</p>
        )}
      </div>
    </BentoCard>
  );
}

/* ─── 4. Handoff ───────────────────────────────────────────── */

function HandoffCard() {
  const lang = useSiteLanguage();
  const card = productsCardsCopy[lang].handoff;
  const ui = productsCardsCopy[lang].ui;
  const [sdk, setSdk] = useState<SdkId>("react");
  const [copied, setCopied] = useState(false);
  const [format, setFormat] = useState("sdk");
  const [version, setVersion] = useState("v3");
  const [codeCopied, setCodeCopied] = useState(false);
  const link = "https://ddc.dev/p/acme-auth";

  const copy = async (text: string, kind: "link" | "code") => {
    try {
      await navigator.clipboard.writeText(text);
      if (kind === "link") {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
      } else {
        setCodeCopied(true);
        window.setTimeout(() => setCodeCopied(false), 1600);
      }
    } catch {
      /* ignore */
    }
  };

  return (
    <BentoCard className="lg:col-span-2">
      <CardCopy
        title={card.title}
        body={card.body}
      />

      <div className="mt-4 grid flex-1 gap-3 px-3 pb-4 sm:mt-6 sm:grid-cols-2 sm:gap-4 sm:px-6 sm:pb-6">
        <div className="rounded-2xl border border-black/[0.08] bg-white p-4 shadow-sm dark:border-white/[0.1] dark:bg-[#1A1A1A]">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-400">
            {ui.assetConfig}
          </p>

          <label className="mt-4 block text-[11px] font-medium text-neutral-500">
            {ui.assetLink}
          </label>
          <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-black/[0.08] bg-[#FAFAFA] px-3 py-2 dark:border-white/[0.1] dark:bg-black/30">
            <Link2 className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
            <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-neutral-700 dark:text-neutral-300">
              {link}
            </span>
            <motion.button
              type="button"
              whileTap={{ scale: 0.94 }}
              onClick={() => copy(link, "link")}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[10px] font-medium text-neutral-600 transition-colors hover:bg-black/[0.05] dark:text-neutral-300 dark:hover:bg-white/[0.06]"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-[#00C853]" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
              {copied ? ui.copied : ui.copy}
            </motion.button>
          </div>

          <label className="mt-3 block text-[11px] font-medium text-neutral-500">
            {ui.format}
          </label>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            className="mt-1.5 w-full appearance-none rounded-xl border border-black/[0.08] bg-[#FAFAFA] px-3 py-2 text-[12px] outline-none transition-[border-color] focus:border-foreground/40 focus:ring-0 dark:border-white/[0.1] dark:bg-black/30"
          >
            <option value="sdk">{ui.sdkEmbed}</option>
            <option value="cdn">{ui.cdnScript}</option>
            <option value="api">{ui.restApi}</option>
          </select>

          <label className="mt-3 block text-[11px] font-medium text-neutral-500">
            Version
          </label>
          <select
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            className="mt-1.5 w-full appearance-none rounded-xl border border-black/[0.08] bg-[#FAFAFA] px-3 py-2 text-[12px] outline-none transition-[border-color] focus:border-foreground/40 focus:ring-0 dark:border-white/[0.1] dark:bg-black/30"
          >
            <option value="v3">v3.2.1 (stable)</option>
            <option value="v2">v2.9.0</option>
          </select>

          <p className="mt-3 text-[10px] text-neutral-400">
            Using {format} · {version}
          </p>
        </div>

        <div className="flex flex-col overflow-hidden rounded-2xl border border-black/[0.08] bg-[#111] text-white shadow-sm dark:border-white/[0.12]">
          <div className="flex items-center gap-1.5 border-b border-white/[0.08] px-3 py-2.5">
            {(
              [
                { id: "react" as const, label: "React" },
                { id: "node" as const, label: "Node" },
                { id: "cli" as const, label: "CLI" },
              ] as const
            ).map((tab) => (
              <motion.button
                key={tab.id}
                type="button"
                whileTap={{ scale: 0.95 }}
                onClick={() => setSdk(tab.id)}
                className={cn(
                  "rounded-full px-2.5 py-1 text-[10px] font-medium transition-colors",
                  sdk === tab.id
                    ? "bg-white text-black"
                    : "text-white/50 hover:text-white/80",
                )}
              >
                {tab.label}
              </motion.button>
            ))}
            <motion.button
              type="button"
              whileTap={{ scale: 0.94 }}
              onClick={() => copy(CODE_BY_SDK[sdk], "code")}
              className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] text-white/60 hover:bg-white/10 hover:text-white"
            >
              {codeCopied ? (
                <Check className="h-3 w-3 text-[#00C853]" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
              {codeCopied ? ui.copied : ui.copy}
            </motion.button>
          </div>
          <button
            type="button"
            onClick={() => copy(CODE_BY_SDK[sdk], "code")}
            className="relative min-h-[9rem] flex-1 p-4 text-left"
          >
            <AnimatePresence mode="wait">
              <motion.pre
                key={`${sdk}-${format}-${version}`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.25 }}
                className="font-mono text-[11px] leading-relaxed text-emerald-300/90 sm:text-[12px]"
              >
                {`// ${format} · ${version}\n`}
                {CODE_BY_SDK[sdk]}
              </motion.pre>
            </AnimatePresence>
          </button>
        </div>
      </div>
    </BentoCard>
  );
}

/* ─── 5. Integration ───────────────────────────────────────── */

function IntegrationCard() {
  const lang = useSiteLanguage();
  const card = productsCardsCopy[lang].integration;
  const ui = productsCardsCopy[lang].ui;
  const [active, setActive] =
    useState<(typeof INTEGRATIONS)[number]["id"]>("figma");
  const [picked, setPicked] = useState(0);

  const folders: Record<
    string,
    { name: string; files: { label: string; icon: LucideIcon; tone: string }[] }
  > = {
    figma: {
      name: ui.designSystem,
      files: [
        { label: "Buttons", icon: LayoutGrid, tone: "bg-[#E8EEF5]" },
        { label: ui.authScreens, icon: Shield, tone: "bg-[#FFF4DE]" },
        { label: "Tokens", icon: Star, tone: "bg-[#E7F0EC]" },
      ],
    },
    vscode: {
      name: "Workspace",
      files: [
        { label: "ddc.config.ts", icon: FileCode2, tone: "bg-[#E8EEF5]" },
        { label: "pipelines/", icon: GitBranch, tone: "bg-[#FFF4DE]" },
        { label: "README", icon: FolderOpen, tone: "bg-[#E7F0EC]" },
      ],
    },
    github: {
      name: "acme/platform",
      files: [
        { label: "main", icon: GitBranch, tone: "bg-[#E8EEF5]" },
        { label: "feat/auth", icon: Shield, tone: "bg-[#FFF4DE]" },
        { label: "PR #128", icon: Link2, tone: "bg-[#E7F0EC]" },
      ],
    },
    slack: {
      name: ui.engDeploys,
      files: [
        { label: "Deploy bot", icon: Bell, tone: "bg-[#E8EEF5]" },
        { label: "Alerts", icon: Bell, tone: "bg-[#FFF4DE]" },
        { label: "Standup", icon: Check, tone: "bg-[#E7F0EC]" },
      ],
    },
    vercel: {
      name: "Production",
      files: [
        { label: "acme.app", icon: Star, tone: "bg-[#E8EEF5]" },
        { label: "Preview 42", icon: LayoutGrid, tone: "bg-[#FFF4DE]" },
        { label: "Edge", icon: RefreshCw, tone: "bg-[#E7F0EC]" },
      ],
    },
    aws: {
      name: "us-east-1",
      files: [
        { label: "ECS", icon: Database, tone: "bg-[#E8EEF5]" },
        { label: "RDS", icon: Database, tone: "bg-[#FFF4DE]" },
        { label: "S3 assets", icon: Folder, tone: "bg-[#E7F0EC]" },
      ],
    },
  };

  const view = folders[active] ?? folders.figma;

  return (
    <BentoCard className="min-h-[22rem]">
      <CardCopy
        title={card.title}
        body={card.body}
      />

      <div className="relative mt-4 flex flex-1 flex-col px-3 pb-4 sm:px-5 sm:pb-5">
        <div className="flex min-h-[10rem] flex-1 flex-col overflow-hidden rounded-2xl border border-black/[0.08] bg-white shadow-sm dark:border-white/[0.1] dark:bg-[#1A1A1A]">
          <div className="flex items-center gap-2 border-b border-black/[0.06] px-3 py-2 dark:border-white/[0.08]">
            <Workflow className="h-3.5 w-3.5 text-neutral-400" />
            <AnimatePresence mode="wait">
              <motion.span
                key={active}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-[12px] font-semibold tracking-tight"
              >
                {view.name}
              </motion.span>
            </AnimatePresence>
          </div>

          <div className="grid flex-1 grid-cols-3 gap-2 p-3">
            {view.files.map((file, i) => {
              const Icon = file.icon;
              const isPicked = picked === i;
              return (
                <motion.button
                  key={`${active}-${file.label}`}
                  type="button"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.3, ease }}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.96 }}
                  onClick={() => setPicked(i)}
                  className={cn(
                    "flex flex-col rounded-xl border p-2 text-left transition-shadow",
                    isPicked
                      ? "border-[#00C853] bg-white shadow-md ring-2 ring-[#00C853]/20 dark:bg-[#222]"
                      : "border-black/[0.06] bg-[#F4F4F5] dark:border-white/[0.08] dark:bg-[#222]",
                  )}
                >
                  <div
                    className={cn(
                      "mb-2 flex aspect-square items-center justify-center rounded-lg",
                      file.tone,
                    )}
                  >
                    <Icon className="h-5 w-5 text-neutral-700/70" strokeWidth={1.5} />
                  </div>
                  <p className="truncate text-[10px] font-medium">{file.label}</p>
                </motion.button>
              );
            })}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {INTEGRATIONS.map((tab) => {
            const Icon = tab.icon;
            return (
              <motion.button
                key={tab.id}
                type="button"
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setActive(tab.id);
                  setPicked(0);
                }}
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium transition-colors sm:text-[11px]",
                  active === tab.id
                    ? "bg-neutral-900 text-white dark:bg-white dark:text-black"
                    : "bg-black/[0.04] text-neutral-600 hover:bg-black/[0.07] dark:bg-white/[0.06] dark:text-neutral-300 dark:hover:bg-white/[0.1]",
                )}
              >
                <Icon className="h-3 w-3" strokeWidth={2} />
                {tab.label}
              </motion.button>
            );
          })}
        </div>
      </div>
    </BentoCard>
  );
}

/* ─── Section ──────────────────────────────────────────────── */

export function Products() {
  const lang = useSiteLanguage();
  const t = productsCopy[lang];

  return (
    <section id="products" className="bg-background py-14 sm:py-20 lg:py-24">
      <div className="mx-auto w-full max-w-[1400px] px-4 sm:px-6 lg:px-10">
        <div className="mb-8 max-w-3xl sm:mb-12 lg:mb-14">
          <h2 className="text-balance text-3xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
            {t.title}
          </h2>
          <p className="mt-3 text-base text-muted-foreground sm:mt-4 sm:text-lg sm:text-xl">
            {t.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-5">
          <WorkspaceCard />
          <OptimizeCard />
          <ProgressCard />
          <HandoffCard />
          <IntegrationCard />
        </div>
      </div>
    </section>
  );
}
